import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Card, Row, Col, Typography, Tabs, message } from 'antd';
import { useTranslation } from 'react-i18next';
import WizardProgress from './WizardProgress';
import CyclesTable from '../cycles/CyclesTable';
import CyclesForm from '../cycles/CyclesForm';
import './WizardLayout.css';
import { get_all_cycles } from '../../redux/actions/cyclesActions';
import { load_create } from '../../redux/actions/selfActions';


const WizardCycles = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [messageApi, contextHolder] = message.useMessage();

	const { all_cycles, all_cycles_loading } = useSelector(state => state.cycles);
	const { load_create_loading } = useSelector(state => state.self);

	const [activeTab, setActiveTab] = useState('existing');
	const [selectedCycle, setSelectedCycle] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [cycleAction, setCycleAction] = useState('');
	const [cycleToUpdate, setCycleToUpdate] = useState({});
	const [pageSize] = useState(10);
	const [skip, setSkip] = useState(0);
	const [newlyCreatedCycle, setNewlyCreatedCycle] = useState(null);
	const [isCreatingLoad, setIsCreatingLoad] = useState(false);

	// Get wizard state from localStorage
	const [wizardState, setWizardState] = useState(() => JSON.parse(localStorage.getItem('wizardState') || '{}'));

	// Create orgFilters object for the existing components
	const orgFilters = {
		organization: wizardState.organization?.id || '',
		workspace: wizardState.workspace?.id || '',
		module: wizardState.module?.id || ''
	};

	useEffect(() => {
		if (wizardState.organization?.id) {
			const filters = {
				organization: wizardState.organization.id,
				workspace: wizardState.workspace?.id || '',
				module: wizardState.module?.id || '',
				skip: 0,
				limit: pageSize
			};
			dispatch(get_all_cycles(filters));
		}
	}, [dispatch, wizardState.organization?.id, wizardState.workspace?.id, wizardState.module?.id, pageSize]);

	const createCycle = () => {
		setIsModalOpen(true);
		setCycleAction('create');
		setCycleToUpdate({});
	};

	// This function will be called when user clicks on a cycle row in the table
	const handleCycleSelection = (cycle) => {
		setSelectedCycle(cycle);

		// Get cycle status number for cycleSelectionType
		const cycleStatus = cycle.status || 0;

		// Update wizard state
		const updatedWizardState = {
			...wizardState,
			cycle: cycle,
			loadType: activeTab === 'existing' ? 'additional' : 'initial',
			cycleSelectionType: cycleStatus
		};
		setWizardState(updatedWizardState);
		localStorage.setItem('wizardState', JSON.stringify(updatedWizardState));
		localStorage.setItem('cycle', cycle._id?.$oid || cycle.id || cycle);
	};

	const handleTabChange = (key) => {
		setActiveTab(key);
		setSelectedCycle(null); // Reset selection when switching tabs
		setNewlyCreatedCycle(null); // Reset newly created cycle when switching tabs
	};

	// Handler for when a new cycle is successfully created
	const handleCycleCreated = (newCycle) => {
		// Normalize the cycle structure to ensure it has the expected _id format
		const normalizedCycle = {
			...newCycle,
			// Ensure _id exists in the expected format
			_id: newCycle._id || { $oid: newCycle.id || newCycle }
		};

		setNewlyCreatedCycle(normalizedCycle);
		setSelectedCycle(normalizedCycle);

		// For newly created cycles, cycleSelectionType should be 1
		// Update wizard state with the new cycle
		const updatedWizardState = {
			...wizardState,
			cycle: normalizedCycle,
			loadType: 'initial',
			cycleSelectionType: 1
		};
		setWizardState(updatedWizardState);
		localStorage.setItem('wizardState', JSON.stringify(updatedWizardState));
		localStorage.setItem('cycle', normalizedCycle._id.$oid || normalizedCycle.id || normalizedCycle);
	};

	const wizardSaveLoadId = (loadId) => {
		// Update the wizard state with the new load ID
		const updatedWizardState = {
			...wizardState,
			load_id: loadId
		};
		setWizardState(updatedWizardState);
		localStorage.setItem('wizardState', JSON.stringify(updatedWizardState));
	}	

	const handleContinue = async () => {
		if (selectedCycle) {
			// If it's a new cycle, create the load first
			if (activeTab === 'new' && newlyCreatedCycle) {
				setIsCreatingLoad(true);
				
				// Helper function to safely extract cycle ID
				const getCycleId = (cycle) => {
					return typeof cycle === 'string' 
						? cycle 
						: cycle._id?.$oid || cycle._id || cycle.id;
				};

				const loadData = {
					organization: wizardState.organization?.id || '',
					workspace: wizardState.workspace?.id || '',
					module: wizardState.module?.id || '',
					cycle: getCycleId(newlyCreatedCycle),
					type: 1, // Initial load type
					name: `Carga Inicial ${newlyCreatedCycle.name}`,
					description: `Carga inicial para el ciclo ${newlyCreatedCycle.name}`
				};

				const createLoadMessageApi = {
					loading: (msg, duration) => message.loading(msg, duration),
					success: (msg, duration) => {
						message.success(msg, duration);
						setIsCreatingLoad(false);
						navigate('/wizard/upload');
					},
					error: (msg, duration) => {
						message.error(msg || 'Error al crear la carga', duration);
						setIsCreatingLoad(false);
					}
				};

				try {
					dispatch(load_create(createLoadMessageApi, loadData, () => {}, {}, wizardSaveLoadId));
				} catch (error) {
					console.error('Error creating load:', error);
					setIsCreatingLoad(false);
					message.error('Error inesperado al crear la carga');
				}
			} else {
				// For existing cycles, navigate directly
				navigate('/wizard/upload');
			}
		}
	};

	const handleBack = () => {
		navigate('/wizard/schemas');
	};

	const canContinue = selectedCycle !== null;

	const tabItems = [
		{
			key: 'existing',
			label: 'Usar ciclo existente',
			children: (
				<div>
					<Typography.Paragraph style={{ marginBottom: '20px', fontSize: '16px' }}>
						Selecciona un ciclo existente para realizar una carga adicional. Haz clic en una fila para seleccionar el ciclo.
					</Typography.Paragraph>
					{/* <div style={{
						backgroundColor: 'white',
						borderRadius: '8px',
						overflow: 'hidden',
						boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
					}}> */}
						<CyclesTable
							messageApi={messageApi}
							orgFilters={orgFilters}
							setIsModalOpen={setIsModalOpen}
							setCycleAction={setCycleAction}
							setCycleToUpdate={setCycleToUpdate}
							pageSize={pageSize}
							skip={skip}
							setSkip={setSkip}
							onRowClick={handleCycleSelection}
							selectedCycle={selectedCycle}
						/>
					{/* </div> */}
				</div>
			)
		},
		{
			key: 'new',
			label: 'Crear nuevo ciclo',
			children: (
				<div>
					<Typography.Paragraph style={{ marginBottom: '20px', fontSize: '16px' }}>
						Crea un nuevo ciclo para realizar una carga inicial, o selecciona un ciclo recién creado de la tabla.
					</Typography.Paragraph>
					<Button
						type="primary"
						size="large"
						onClick={createCycle}
						style={{ height: '50px', fontSize: '16px', marginBottom: '20px' }}
					>
						Crear Nuevo Ciclo
					</Button>
					{/* <div style={{
						backgroundColor: 'white',
						borderRadius: '8px',
						overflow: 'hidden',
						boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
					}}> */}
						<CyclesTable
							messageApi={messageApi}
							orgFilters={orgFilters}
							setIsModalOpen={setIsModalOpen}
							setCycleAction={setCycleAction}
							setCycleToUpdate={setCycleToUpdate}
							pageSize={pageSize}
							skip={skip}
							setSkip={setSkip}
							onRowClick={handleCycleSelection}
							selectedCycle={selectedCycle}
							filteredData={newlyCreatedCycle ? [newlyCreatedCycle] : null}
						/>
					{/* </div> */}
				</div>
			)
		}
	];

	return (
		<div className="wizard-container">
			{contextHolder}
			<WizardProgress currentStep={2} />

			<div className="content-container" style={{ padding: '40px', maxWidth: '100%', margin: '0 auto' }}>
				<Row justify="space-between" align="top" style={{ marginBottom: '30px' }} gutter={[16, 16]}>
					<Col xs={24} lg={12}>
						<h1 style={{ fontSize: '36px', fontWeight: 'bold', margin: 0 }}>
							PASO 2: DEFINIR CICLO
						</h1>
					</Col>
					<Col xs={24} lg={12} style={{ textAlign: 'right' }}>
						<Typography.Text>
							<strong>{wizardState.organization?.name || 'Sin información'}</strong>, estás trabajando en el espacio de trabajo <strong>{wizardState.workspace?.name || 'Sin información'}</strong>, módulo <strong>{wizardState.module?.name || 'Sin información'}</strong>.
							<br />
							Si deseas cambiar esta información, regresa al inicio del proceso.
						</Typography.Text>
					</Col>
				</Row>

				{/* <Card style={{ marginBottom: '30px', borderRadius: '8px' }}> */}
					<Tabs
						activeKey={activeTab}
						onChange={handleTabChange}
						items={tabItems}
						size="large"
						style={{ minHeight: '400px' }}
					/>
				{/* </Card> */}

				<Row justify="space-between" style={{ marginTop: '40px' }}>
					<Col>
						<Button
							size="large"
							onClick={handleBack}
							style={{ height: '50px', fontSize: '16px', minWidth: '120px' }}
						>
							← REGRESAR
						</Button>
					</Col>
					<Col>
						<Button
							type="primary"
							size="large"
							onClick={handleContinue}
							disabled={!canContinue}
							loading={isCreatingLoad}
							style={{
								height: '50px',
								fontSize: '16px',
								minWidth: '150px',
								backgroundColor: canContinue ? '#3e619b' : '#d9d9d9',
								borderColor: canContinue ? '#3e619b' : '#d9d9d9'
							}}
						>
							{isCreatingLoad ? 'CREANDO CARGA...' : 'CONTINUAR →'}
						</Button>
					</Col>
				</Row>
			</div>

			<CyclesForm
				messageApi={messageApi}
				isModalOpen={isModalOpen}
				setIsModalOpen={setIsModalOpen}
				orgFilters={orgFilters}
				cycleAction={cycleAction}
				cycleToUpdate={cycleToUpdate}
				setCycleToUpdate={setCycleToUpdate}
				pageSize={pageSize}
				skip={skip}
				onCycleCreated={handleCycleCreated}
			/>
		</div>
	);
};

export default WizardCycles;